// @givmo/app-admin — the company gift-card notice in the approved terms panel (EP-WEB4, GC-FU12b).
//
// Renders the signed notice a send's funding source shows (`gift-disclaimer.ts`: Notice A on the
// company wallet, Notice C on a company card) — server-driven, or its floor — directly above the send
// control in the confirm dialogs, single and batch, and above Create at an occasion program's setup.
// The `gift-disclosure` testid stays on the block root as the e2e/RTL contract, the lines render
// verbatim, one `<li>` per registry line, and the root carries the notice's marker (`noticeMarker`:
// its surface, variant, `content_hash` and source as `data-disclosure-*` attributes) so a spec can
// tell which notice, and which bundle, it is reading.
//
// THE PRESENTATION IS THE APPROVED TERMS PANEL (CAM-R60): the shared `@givmo/disclosure`
// `DisclosureTerms` in the portal's tokens — the same panel as the Give and approve dialogs (D-CAM-44)
// — under a client-owned "Terms" heading (D-CAM-97: "How about just 'Terms'. definitely not 'before
// you give'"), an h3 under the dialog's title. The FULL text is fine print in a tinted panel bounded
// to five lines, with a drawn scrollbar and a bottom fade, and that fine print is "conspicuous enough"
// for §317 (D-CAM-97). The dialogs place it after their errors, adjacent to the button (D-CAM-41).
//
// WHY THE BOUND. The confirm card is clamped to the viewport and scrolls its own content
// (`Dialog.Content`), but whether its FOOTER is on screen when it OPENS depends on where Base UI's
// initial focus lands — the first tabbable. An unbounded six-item block alone was 684px, so the card
// outgrew a 720px viewport and Send opened ~1040px down it. The five-line panel keeps the card under
// the clamp, so Send is inside the viewport on open whatever the tab order (pinned by e2e
// `company.gift_cards.react.spec.ts`). Do not cure this by pinning `initialFocus` to the footer — that
// re-creates the accident on purpose.

import type { ResolvedDisclosure } from '@givmo/disclosure';
import { DisclosureTerms } from '@givmo/disclosure';
import { noticeMarker, type CompanyGiftCardNotice } from './gift-disclaimer';

export interface GiftDisclosureBlockProps {
  /** Which signed notice this is (its surface and variant). */
  notice: CompanyGiftCardNotice;
  /** The notice as resolved: its LIVE registry row, or its floor. */
  shown: ResolvedDisclosure;
}

/** The heading's words — client-owned, NOT part of the counsel-pinned registry copy. */
export const GIFT_TERMS_HEADING = 'Terms';

export function GiftDisclosureBlock({ notice, shown }: GiftDisclosureBlockProps) {
  return (
    <DisclosureTerms
      appearance="saas"
      headingLevel={3}
      heading={GIFT_TERMS_HEADING}
      sections={[{ key: 'gift-card-notice', label: null, lines: shown.lines }]}
      testId="gift-disclosure"
      dataAttributes={noticeMarker(notice, shown)}
    />
  );
}
